<script setup>
import BaseModal from "../global/modals/BaseModal.vue";
import TwoFactorCodeField from "./TwoFactorCodeField.vue";
import {computed, ref, watch} from "vue";

const emit = defineEmits(['close', 'confirm']);

const props = defineProps({
  errors: {
    required: false,
    type: Array,
    default: () => ([]),
  },
  loading: {
    required: false,
    type: Boolean,
    default: false,
  },
});

const confirmationWord = 'DISABLE';

const code = ref('');
const step = ref('code');
const confirmationText = ref('');

const confirmationMatches = computed(() => confirmationText.value.trim() === confirmationWord);

/**
 * The server verifies the code, so a wrong one only comes back after the final
 * confirmation. Drop back to the code step so the error is shown against the
 * field it belongs to instead of against a confirmation screen.
 */
watch(() => props.errors, (errors) => {
  if (errors.length > 0) {
    confirmationText.value = '';
    step.value = 'code';
  }
});

const handleContinue = () => {
  if (code.value.length === 0) {
    return;
  }

  step.value = 'confirm';
}

const handleBack = () => {
  confirmationText.value = '';
  step.value = 'code';
}

const handleConfirm = () => {
  if (code.value.length === 0 || !confirmationMatches.value) {
    return;
  }

  emit('confirm', code.value);
}
</script>

<template>
  <Teleport to="body">
    <BaseModal
      id="disable-two-factor-modal"
      @close="emit('close')"
    >
      <template #heading>
        Disable Two-Factor Authentication?
      </template>

      <div v-if="step === 'code'">
        <p class="text-center">
          Are you sure you wish to disable Two-Factor Authentication? <br>
          Your account will be less secure and you will no longer be able to login using your recovery codes.<br>
          Enter your authentication code to confirm.
        </p>

        <TwoFactorCodeField
          v-model="code"
          :errors="errors"
          @submit="handleContinue"
        />
      </div>

      <div v-else>
        <p class="text-center">
          This is your last chance to back out.<br>
          Disabling Two-Factor Authentication removes the extra layer of security <br>
          on your account and invalidates your recovery codes.<br>
          Type <strong>{{ confirmationWord }}</strong> below to confirm.
        </p>

        <div class="d-flex justify-content-center align-items-center my-4">
          <input
            v-model="confirmationText"
            type="text"
            class="form-control w-75 text-center"
            placeholder="Type DISABLE to confirm"
            autocomplete="off"
          >
        </div>
      </div>

      <template #footer>
        <div
          v-if="step === 'code'"
          class="w-100 d-flex justify-content-center m-0 m-auto"
        >
          <button
            class="btn btn-success btn-lg button font-weight-bold mx-3 mb-0 w-50"
            :disabled="loading"
            @click="emit('close')"
          >
            Cancel
          </button>
          <button
            class="btn btn-danger btn-lg button font-weight-bold mx-3 mb-0 w-100"
            :class="{disabled: code.length === 0 || loading}"
            :disabled="code.length === 0 || loading"
            @click="handleContinue"
          >
            Continue
          </button>
        </div>

        <div
          v-else
          class="w-100 d-flex justify-content-center m-0 m-auto"
        >
          <button
            class="btn btn-success btn-lg button font-weight-bold mx-3 mb-0 w-50"
            :disabled="loading"
            @click="handleBack"
          >
            Back
          </button>
          <button
            class="btn btn-danger btn-lg button font-weight-bold mx-3 mb-0 w-100"
            :class="{disabled: !confirmationMatches || loading}"
            :disabled="!confirmationMatches || loading"
            @click="handleConfirm"
          >
            Yes, disable Two Factor Authentication
          </button>
        </div>
      </template>
    </BaseModal>
  </Teleport>
</template>

<style>
  #disable-two-factor-modal .modal_window {
    max-width: 900px;
    min-width: 675px;
  }

  #disable-two-factor-modal .btn-danger {
    background: #F34F4F;
  }
</style>
